import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MyTasks = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, pending
  const [selectedTask, setSelectedTask] = useState(null);

  useEffect(() => {
    if (user) {
      fetchJourney();
    }
  }, [user]);

  const fetchJourney = async () => {
    try {
      const response = await axios.get(`${API}/learning-journey/`, { withCredentials: true });
      setJourney(response.data);
    } catch (error) {
      console.error('Error fetching journey:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredTasks = () => {
    if (!journey?.assigned_tasks) return [];
    
    const tasks = journey.assigned_tasks;
    if (filter === 'completed') return tasks.filter(t => t.completed);
    if (filter === 'pending') return tasks.filter(t => !t.completed);
    return tasks;
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      'VIDEO': '🎥',
      'READING': '📖',
      'PRACTICE_SET': '📝',
      'MIXED_TEST': '🧪',
      'SUMMARY_WRITING': '✍️',
      'PERSONALIZED_BOOKLET': '📚'
    };
    return icons[type] || '📚';
  };

  const handleTaskClick = (task) => {
    // If it's a booklet task, navigate to booklet page
    if (task.task_type === 'PERSONALIZED_BOOKLET' && task.booklet_id) {
      console.log('Navigating to booklet:', task.booklet_id);
      navigate(`/booklet/${task.booklet_id}/take`);
    } else if (task.type === 'PRACTICE_SET' && task.question_ids && task.question_ids.length > 0) {
      // Practice set with questions - navigate to practice page
      console.log('Starting practice set:', task.id);
      navigate(`/practice/${task.id}`);
    } else {
      // Toggle task details or show message if no questions
      if (task.type === 'PRACTICE_SET' && (!task.question_ids || task.question_ids.length === 0)) {
        alert(language === 'tr' 
          ? 'Bu görevde henüz soru yok. Soru bankası zenginleşince eklenecek.' 
          : 'No questions available for this task yet. Will be added as question bank grows.');
      } else {
        setSelectedTask(selectedTask?.task_id === task.task_id ? null : task);
      }
    }
  };

  const handleDeleteBooklet = async (bookletId) => {
    const confirmMsg = language === 'tr' 
      ? 'Bu kitapçığı silmek istediğinizden emin misiniz?' 
      : 'Are you sure you want to delete this booklet?';
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      await axios.delete(`${API}/personalization/booklet/${bookletId}`, {
        withCredentials: true
      });
      
      alert(language === 'tr' ? '✅ Kitapçık silindi!' : '✅ Booklet deleted!');
      
      // Refresh tasks - fetch journey again
      await fetchJourney();
    } catch (error) {
      console.error('Error deleting booklet:', error);
      console.error('Error details:', error.response?.data);
      alert(language === 'tr' 
        ? `❌ Silme işlemi başarısız! ${error.response?.data?.detail || ''}` 
        : `❌ Failed to delete booklet! ${error.response?.data?.detail || ''}`);
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'EASY': 'bg-green-100 text-green-800',
      'MEDIUM': 'bg-yellow-100 text-yellow-800',
      'HARD': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const filteredTasks = getFilteredTasks();
  const completedCount = journey?.assigned_tasks?.filter(t => t.completed).length || 0;
  const pendingCount = journey?.assigned_tasks?.filter(t => !t.completed).length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Görevlerim' : 'My Tasks'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl shadow-2xl p-8 mb-8 text-white">
          <h1 className="text-4xl font-bold mb-2">
            ✅ {language === 'tr' ? 'Görevlerim' : 'My Tasks'}
          </h1>
          <p className="text-orange-100 text-lg">
            {language === 'tr' 
              ? 'Tamamlanan ve devam eden görevlerin'
              : 'Your completed and ongoing tasks'}
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-blue-600">
              {journey?.assigned_tasks?.length || 0}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Toplam Görev' : 'Total Tasks'}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-green-600">
              {completedCount}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Tamamlanan' : 'Completed'}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="text-3xl font-bold text-orange-600">
              {pendingCount}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {language === 'tr' ? 'Devam Eden' : 'Pending'}
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-lg mb-6">
          <div className="flex border-b">
            {[
              { key: 'all', label: language === 'tr' ? '📋 Tümü' : '📋 All' },
              { key: 'pending', label: language === 'tr' ? '⏳ Devam Eden' : '⏳ Pending' },
              { key: 'completed', label: language === 'tr' ? '✅ Tamamlanan' : '✅ Completed' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex-1 px-6 py-4 font-semibold transition-all ${
                  filter === tab.key
                    ? 'bg-orange-600 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tasks List */}
        {filteredTasks.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-600 text-lg">
              {language === 'tr' ? 'Henüz görev yok' : 'No tasks yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredTasks.map((task, idx) => (
              <div
                key={idx}
                onClick={() => handleTaskClick(task)}
                className={`bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-all ${
                  task.completed ? 'border-l-4 border-green-500' : 'border-l-4 border-orange-500'
                } ${task.task_type === 'PERSONALIZED_BOOKLET' ? 'hover:border-blue-500' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="text-3xl">
                      {task.completed ? '✅' : '⏳'}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h3 className="text-xl font-bold text-gray-800 mb-2">
                          {task.title || task.topic}
                        </h3>
                        
                        {/* Delete button for booklets */}
                        {task.task_type === 'PERSONALIZED_BOOKLET' && task.booklet_id && !task.completed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteBooklet(task.booklet_id);
                            }}
                            className="ml-4 px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition"
                          >
                            🗑️ {language === 'tr' ? 'Sil' : 'Delete'}
                          </button>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(task.difficulty)}`}>
                          {task.difficulty}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          {getTaskTypeIcon(task.task_type || task.type)} {(task.task_type || task.type)?.replace(/_/g, ' ')}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                          ⏱️ {task.estimated_time_minutes || task.estimated_minutes || task.estimatedMinutes} min
                        </span>
                        {task.question_count && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                            📝 {task.question_count} {language === 'tr' ? 'soru' : 'questions'}
                          </span>
                        )}
                        {task.discipline && (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800">
                            🔬 {task.discipline}
                          </span>
                        )}
                      </div>

                      {task.description && (
                        <p className="text-gray-600 text-sm mb-3">{task.description}</p>
                      )}

                      {selectedTask?.id === task.id && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="font-semibold text-gray-700">
                                {language === 'tr' ? 'Konu:' : 'Topic:'}
                              </span>
                              <span className="ml-2 text-gray-600">{task.topic}</span>
                            </div>
                            {task.source && (
                              <div>
                                <span className="font-semibold text-gray-700">
                                  {language === 'tr' ? 'Kaynak:' : 'Source:'}
                                </span>
                                <span className="ml-2 text-gray-600">{task.source}</span>
                              </div>
                            )}
                            {task.completed_at && (
                              <div>
                                <span className="font-semibold text-gray-700">
                                  {language === 'tr' ? 'Tamamlanma:' : 'Completed:'}
                                </span>
                                <span className="ml-2 text-gray-600">
                                  {new Date(task.completed_at).toLocaleDateString()}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <svg 
                    className={`w-5 h-5 text-gray-400 transition-transform ${selectedTask?.id === task.id ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Task Button */}
        <div className="mt-8 text-center">
          <button
            className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl hover:shadow-lg transition-all font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={async () => {
              try {
                const response = await axios.post(`${API}/tasks/assign-next`, {}, { withCredentials: true });
                if (response.data.success) {
                  alert(language === 'tr' 
                    ? `✅ Yeni görev atandı: ${response.data.task.title}`
                    : `✅ New task assigned: ${response.data.task.title}`
                  );
                  fetchJourney(); // Refresh tasks
                }
              } catch (error) {
                console.error('Error assigning task:', error);
                alert(language === 'tr' 
                  ? '❌ Görev atanırken hata oluştu'
                  : '❌ Error assigning task'
                );
              }
            }}
          >
            ➕ {language === 'tr' ? 'Yeni Görev Al' : 'Get New Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyTasks;
